import { SpaceType } from "@components/atoms/Spacer/types";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import RenderHTML from "react-native-render-html";
import styled from "styled-components/native";
import { DefaultTheme } from "styled-components/native";
import useScreenOptionsHooks from "../../../../../hooks/useScreenOptionsHooks";
import { useUserAnalytics } from "../../../../../hooks/useAnalytics";
import { ViewTypeEnum } from "@utils/analytics";
import { Linking, Platform, TouchableOpacity } from "react-native";
import { VDBIcons } from "@utils/VDBIcons";
import { VDBText } from "@components/atoms";
import { MessageStateAction } from "@scenes/more/MoreScreen/types";

const ACTION_HREF_PREFIX = "vdb-action:";

const MessageContainer = ({
  statusColor,
  statusIcon,
  statusMessage,
  statusBackgroundColor,
  actions,
  onActionPress,
}: {
  statusColor: string;
  statusIcon: VDBIcons;
  statusMessage: string;
  statusBackgroundColor?: string;
  actions?: MessageStateAction[];
  onActionPress?: (action: string) => void;
}) => {
  const { typography, t, theme } = useScreenOptionsHooks();
  const { trackHyperLinkEvent } = useUserAnalytics() || {};
  const visibleActions = onActionPress
    ? (actions || []).filter(
        (action) => !action.webOnly || Platform.OS === "web"
      )
    : [];
  const inlineActions = visibleActions.filter((action) => action.inline);
  const blockActions = visibleActions.filter((action) => !action.inline);
  const html = inlineActions.reduce(
    (acc, action) =>
      `${acc} <a href="${ACTION_HREF_PREFIX}${action.action}">${action.label}</a>`,
    t(statusMessage)
  );

  return (
    <MessageBanner
      color={statusColor}
      statusBackgroundColor={statusBackgroundColor}
    >
      {statusIcon && (
        <VDBIcon
          name={statusIcon}
          color={statusColor}
          size={20}
          variant={IconsVariant.VDBCustom}
        />
      )}
      <MessageTextContainer>
        <RenderHTML
          source={{ html }}
          renderersProps={{
            a: {
              onPress: (_event: any, href: string) => {
                if (!href) {
                  return;
                }
                if (href.startsWith(ACTION_HREF_PREFIX)) {
                  onActionPress?.(href.replace(ACTION_HREF_PREFIX, ""));
                  return;
                }
                trackHyperLinkEvent?.(
                  href,
                  ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
                );
                Linking.openURL(href);
              },
            },
          }}
          tagsStyles={{
            body: {
              ...typography["Minor-M"],
              color: theme["core-05"],
            },
            a: {
              color: theme["link-static"],
              textDecorationLine: "none",
            },
          }}
        />
        {blockActions.length > 0 && (
          <ActionsContainer>
            {blockActions.map((action) => (
              <TouchableOpacity
                key={action.id}
                onPress={() => onActionPress?.(action.action)}
              >
                <VDBText
                  type={typography["Minor-M"]}
                  color={theme["link-static"]}
                >
                  {action.label}
                </VDBText>
              </TouchableOpacity>
            ))}
          </ActionsContainer>
        )}
      </MessageTextContainer>
    </MessageBanner>
  );
};

export default MessageContainer;

const MessageBanner = styled.View<{
  color: string;
  statusBackgroundColor?: string;
}>`
  flex-direction: row;
  align-items: flex-start;
  gap: ${SpaceType.Basic}px;
  padding: ${SpaceType.Extra}px ${SpaceType.Double}px;
  background-color: ${({
    theme,
    statusBackgroundColor,
  }: {
    theme: DefaultTheme;
    statusBackgroundColor?: string;
  }) => statusBackgroundColor || theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  align-items: center;
`;

const MessageTextContainer = styled.View`
  flex: 1;
`;

const ActionsContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SpaceType.Double}px;
  margin-top: ${SpaceType.ExtraSmall}px;
`;
